import * as React from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Button,
  FormControl,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { AddCircleOutline, RemoveCircleOutline } from "@mui/icons-material";
import { useStyles } from "../../../../Theme/Style";
import { useEffect, useMemo, useState } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import { useNotification } from "../../../../Contexts/GlobalNotificationContext";
import {
  useGetFabricDetailsQuery,
  useSubmitFabricDetailsMutation,
} from "../../../../services/api/production-order";

const FabricDetailsComponent = ({ prodId, templateItems = [], prodOrder }) => {
  const classes = useStyles();
  const { addNotification } = useNotification();

  const { data: fabricData } = useGetFabricDetailsQuery({ id: prodId });
  const [submitFabricDetails] = useSubmitFabricDetailsMutation();

  const [fabricsItems, setFabricItems] = useState([]);

  const isEqualById = (o, v) => String(o?.id ?? "") === String(v?.id ?? "");

  // Default fabric type from prodOrder.price.fabric
  const defaultFabricType = useMemo(() => {
    const pf = prodOrder?.price?.fabric;
    if (!pf || !fabricData?.fabricTypes?.length) return null;

    // Try ID match
    const byId = fabricData.fabricTypes.find((ft) => String(ft?.id) === String(pf?.id));
    if (byId) return byId;

    // Try name match
    const want = (pf?.name || "").trim().toLowerCase();
    return (
      fabricData.fabricTypes.find(
        (ft) => (ft?.name || "").trim().toLowerCase() === want
      ) || null
    );
  }, [fabricData, prodOrder]);

  // Helper: add option if not already present
  const ensureOption = (arr, opt) => {
    if (!opt) return arr;
    const exists = (arr || []).some((x) => String(x?.id) === String(opt?.id));
    return exists ? arr : [...(arr || []), opt];
  };

  useEffect(() => {
    if (!fabricData) return;

    let initializedRows = [];

    // If templateItems present
    if (templateItems.length) {
      initializedRows = templateItems.map((t, i) => {
        const itemObj =
          fabricData.items?.find((x) => String(x.id) === String(t.itemId)) || null;

        // Limit fabric type options to template-provided list
        const templateTypeIds = (t.fabric_types || []).map((ft) => String(ft.id));
        let typeOptions =
          fabricData.fabricTypes?.filter((ft) => templateTypeIds.includes(String(ft.id))) ||
          [];

        // Always add prodOrder.price.fabric as an extra option
        typeOptions = ensureOption(typeOptions, defaultFabricType);

        // Default fabric type only for the 1st row
        const selectedType = i === 0 ? defaultFabricType : null;

        return {
          item: itemObj,
          fabricType: selectedType,
          color: null,
          position: null,
          typeOptions,
        };
      });
    } else if (fabricData?.fabrics?.length) {
      // If no template, load from API
      initializedRows = fabricData.fabrics.map((f, i) => ({
        item: f.item || null,
        fabricType: i === 0 && defaultFabricType ? defaultFabricType : f.fabric_type || null,
        color: f.color || null,
        position: f.position || null,
        typeOptions: ensureOption(fabricData.fabricTypes || [], defaultFabricType),
      }));
    } else {
      // If no data, start blank row
      initializedRows = [
        {
          item: null,
          fabricType: defaultFabricType || null,
          color: null,
          position: null,
          typeOptions: ensureOption(fabricData?.fabricTypes || [], defaultFabricType),
        },
      ];
    }

    setFabricItems(initializedRows);
  }, [fabricData, templateItems, defaultFabricType]);

  const addNewRow = () => {
    setFabricItems((prev) => [
      ...prev,
      {
        item: null,
        fabricType: null, // not selected by default
        color: null,
        position: null,
        typeOptions: ensureOption(fabricData?.fabricTypes || [], defaultFabricType),
      },
    ]);
  };

  const handleChange = (event, name, index, value) => {
    setFabricItems((prev) => {
      const next = [...prev];
      next[index][name] = value;
      if (name === "fabricType") next[index].color = null; // reset color
      return next;
    });
  };

  const deleteRows = (index) => {
    setFabricItems((prev) => {
      const next = [...prev];
      next.splice(index, 1);
      return next;
    });
  };

  const handleFabricSubmit = async () => {
    try {
      if (
        fabricsItems.every(
          (item) => item.item?.id && item.fabricType?.id && item.color?.id
        )
      ) {
        await submitFabricDetails({
          production_order_id: prodId,
          fabrics: fabricsItems,
        }).unwrap();

        addNotification({
          message: "Fabric details submitted successfully!",
          type: "success",
        });
      } else {
        throw new Error("Please fill out all fields.");
      }
    } catch (e) {
      addNotification({
        message: e.message || "Failed to submit fabric details.",
        type: "error",
      });
    }
  };

  const colorOptionsForRow = (row) => row.fabricType?.related_colors || [];

  return (
    <Accordion className={classes.accordion}>
      <AccordionSummary
        className={classes.accordionSummary}
        expandIcon={<ExpandMoreIcon sx={{ color: "#fff" }} />}
      >
        <Grid container className={classes.accordionSummaryTextContainer}>
          <Grid item xs={4}>Fabric Details</Grid>
          <Grid item xs={4}>面料细节</Grid>
        </Grid>
      </AccordionSummary>

      <AccordionDetails>
        <Box className={classes.tableHeader}>
          <Grid container>
            <Grid item xs={3}><Typography>Item</Typography><Typography>物品</Typography></Grid>
            <Grid item xs={3}><Typography>Fabric Type</Typography><Typography>面料类型</Typography></Grid>
            <Grid item xs={3}><Typography>Color</Typography><Typography>颜色</Typography></Grid>
            <Grid item xs={3}><Typography>Position</Typography><Typography>位置</Typography></Grid>
            <Grid item xs={3}></Grid>
          </Grid>
        </Box>

        {fabricsItems.map((row, index) => {
          const fabricTypeOptions =
            row.typeOptions /* template-limited */ ??
            ensureOption(fabricData?.fabricTypes || [], defaultFabricType);

          return (
            <Box key={index} className={classes.row}>
              <Grid container spacing={2}>
                {/* Item */}
                <Grid item xs={3}>
                  <FormControl fullWidth>
                    <Autocomplete
                      options={fabricData?.items || []}
                      getOptionLabel={(o) => `${o.name} - ${o.chinese_name || ""}`}
                      isOptionEqualToValue={isEqualById}
                      value={row.item}
                      onChange={(e, v) => handleChange(e, "item", index, v)}
                      renderInput={(params) => (
                        <TextField {...params} placeholder="Select Item" />
                      )}
                    />
                  </FormControl>
                </Grid>

                {/* Fabric Type */}
                <Grid item xs={3}>
                  <FormControl fullWidth>
                    <Autocomplete
                      options={fabricTypeOptions}
                      getOptionLabel={(o) => `${o.name} - ${o.chinese_name || ""}`}
                      isOptionEqualToValue={isEqualById}
                      value={row.fabricType}
                      onChange={(e, v) => handleChange(e, "fabricType", index, v)}
                      renderInput={(params) => (
                        <TextField {...params} placeholder="Select Fabric Type" />
                      )}
                    />
                  </FormControl>
                </Grid>

                {/* Color */}
                <Grid item xs={3}>
                  <FormControl fullWidth>
                    <Autocomplete
                      options={colorOptionsForRow(row)}
                      disabled={!row.fabricType}
                      getOptionLabel={(o) => `${o.english_name} - ${o.chinese_name || ""}`}
                      isOptionEqualToValue={isEqualById}
                      value={row.color}
                      onChange={(e, v) => handleChange(e, "color", index, v)}
                      renderInput={(params) => (
                        <TextField {...params} placeholder="Select Color" />
                      )}
                    />
                  </FormControl>
                </Grid>

                {/* Position */}
                <Grid item xs={3}>
                  <FormControl fullWidth>
                    <Autocomplete
                      options={fabricData?.positions || []}
                      getOptionLabel={(o) => `${o.name} - ${o.chinese_name || ""}`}
                      isOptionEqualToValue={isEqualById}
                      value={row.position}
                      onChange={(e, v) => handleChange(e, "position", index, v)}
                      renderInput={(params) => (
                        <TextField {...params} placeholder="Select Position" />
                      )}
                    />
                  </FormControl>
                </Grid>

                {/* Remove */}
                <Grid item xs={3}>
                  <Button
                    onClick={() => deleteRows(index)}
                    disabled={fabricsItems.length === 1}
                    startIcon={<RemoveCircleOutline />}
                    color="error"
                  >
                    Remove
                  </Button>
                </Grid>
              </Grid>
            </Box>
          );
        })}

        <Box sx={{ mt: 2 }}>
          <Button
            variant="outlined"
            onClick={addNewRow}
            startIcon={<AddCircleOutline />}
          >
            Add New Row
          </Button>
        </Box>

        <Box sx={{ mt: 2 }}>
          <Button variant="contained" onClick={handleFabricSubmit}>
            Save
          </Button>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default FabricDetailsComponent;
